import InfoCard from '@/components/InfoCard';
import { useDialog, Chip } from '@gokrew/ui';
import DOMPurify from 'dompurify';
import {
  formatAnnouncementDate,
  getAttachmentStyle,
  relativeTime,
} from '@gokrew/ui/utility/Announcements';
import { getInitialsFromName } from '@gokrew/ui/utility/utility';
import { DashboardEmptyState } from '../DashboardEmptyState';

import type {
  Announcement,
  AnnouncementAttachment,
} from '@gokrew/models/announcements';

import {
  useDashboardAnnouncementById,
  useDashboardAnnouncements,
} from '@/hooks/dashboard';
import { useTranslation } from 'react-i18next';
import { TranslationNS } from '@gokrew/models/constants';
import styles from './AnnouncementsCard.module.css';
import { Download, Megaphone, Pin } from 'lucide-react';
import { Tone } from '@gokrew/models/ui.types';
import { CardLoader } from '../CardLoader';

function AttachmentCard({
  attachment,
}: {
  attachment: AnnouncementAttachment;
}) {
  const { Icon, bg, color } = getAttachmentStyle(attachment.name);
  const ext = attachment.name.split('.').pop()?.toUpperCase() ?? 'FILE';

  return (
    <a
      href={attachment.url}
      target="_blank"
      rel="noreferrer"
      className={styles.attachmentCard}
    >
      <div className={styles.attachmentIconWrapper} style={{ background: bg }}>
        <Icon size={14} color={color} />
      </div>

      <p className={styles.attachmentName}>{attachment.name}</p>

      <div className={styles.attachmentFooter}>
        <span className={styles.attachmentExt}>{ext}</span>

        <Download size={12} className={styles.attachmentDownload} />
      </div>
    </a>
  );
}

function AnnouncementDetail({ id }: { id: string }) {
  const { t, i18n } = useTranslation(TranslationNS.Announcement);
  const { data: announcement } = useDashboardAnnouncementById(id);

  if (!announcement) return null;

  const uniqueAttachments = announcement.attachments
    ? Object.values(
        announcement.attachments.reduce<Record<string, AnnouncementAttachment>>(
          (acc, a) => ({ ...acc, [a.name]: a }),
          {}
        )
      )
    : [];

  const authorInitials = getInitialsFromName(announcement.author);

  return (
    <div className={styles.detailWrapper}>
      <div className={styles.metaRow}>
        {announcement.isPinned && (
          <Chip type={Tone.ACCENT} size="sm" className="font-semibold">
            <Pin size={10} className="fill-(--announcement-accent)" />
            {t('card.pinned', { defaultValue: 'PINNED' })}
          </Chip>
        )}

        <div className={styles.authorGroup}>
          <div className={styles.authorAvatar}>{authorInitials}</div>

          <span className={styles.authorText}>
            {t('card.posted_by')}

            <span className={styles.authorName}>{announcement.author}</span>
          </span>
        </div>

        <span className={styles.metaDot}>·</span>

        <span className={styles.metaDate}>
          {formatAnnouncementDate(announcement.publishAt, i18n.language)}
        </span>
      </div>

      <div className={styles.scrollBody}>
        <div
          className={styles.description}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(announcement.description),
          }}
        />

        {uniqueAttachments.length > 0 && (
          <div className={styles.attachmentsSection}>
            <p className={styles.attachmentsTitle}>
              {t('card.attachments', {
                count: uniqueAttachments.length,
              })}
            </p>

            <div className={styles.attachmentsGrid}>
              {uniqueAttachments.map((attachment) => (
                <AttachmentCard key={attachment.id} attachment={attachment} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function AnnouncementsCard({ dataTour }: { dataTour?: string }) {
  const { t, i18n } = useTranslation(TranslationNS.Announcement);
  const { openDialog } = useDialog();

  const { data: announcements = [], isLoading } = useDashboardAnnouncements({
    page: 1,
    limit: 99,
  });

  const openAnnouncement = (item: Announcement) => {
    openDialog({
      title: item.title,
      content: <AnnouncementDetail id={item.id} />,
      containerClass: '!max-w-[640px] w-full',
    });
  };

  const byRecency = (a: Announcement, b: Announcement) =>
    new Date(b.publishAt).getTime() - new Date(a.publishAt).getTime();

  const pinned = announcements.filter((item) => item.isPinned).sort(byRecency);
  const unpinned = announcements
    .filter((item) => !item.isPinned)
    .sort(byRecency);

  const getDescription = (item: Announcement) =>
    item.description.replace(/<[^>]*>/g, '').split('\n')[0];

  const renderPinnedItem = (item: Announcement) => (
    <div
      key={item.id}
      onClick={() => openAnnouncement(item)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openAnnouncement(item);
        }
      }}
      className={styles.pinnedRow}
    >
      <div className={styles.pinnedIconWrap}>
        <span className={styles.pinnedIconDot} />
      </div>

      <div className={styles.pinnedContent}>
        <p className={styles.pinnedTitle}>{item.title}</p>
        <p className={styles.pinnedDescription}>{getDescription(item)}</p>
        <p className={styles.pinnedTime}>
          {relativeTime(item.publishAt, i18n.language)}
        </p>
      </div>
    </div>
  );

  const renderUnpinnedItem = (item: Announcement) => (
    <div
      key={item.id}
      onClick={() => openAnnouncement(item)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openAnnouncement(item);
        }
      }}
      className={styles.unpinnedRow}
    >
      <div className={styles.unpinnedHeader}>
        <p className={styles.unpinnedTitle}>{item.title}</p>
        <span className={styles.unpinnedTime}>
          {relativeTime(item.publishAt, i18n.language)}
        </span>
      </div>

      <p className={styles.unpinnedDescription}>{getDescription(item)}</p>
    </div>
  );

  return (
    <InfoCard
      dataTour={dataTour}
      className={styles.card}
      title={t('card.title')}
      action={
        pinned.length > 0 && (
          <Chip type={Tone.WARNING} size="sm" className="font-semibold">
            {t('card.pinned_count', { count: pinned.length })}
          </Chip>
        )
      }
    >
      {isLoading && <CardLoader />}

      {!isLoading && announcements.length === 0 && (
        <DashboardEmptyState
          layout="row"
          icon={Megaphone}
          title={t('card.empty')}
          description={t('card.empty_description')}
        />
      )}

      {pinned.map(renderPinnedItem)}

      {unpinned.length > 0 && (
        <>
          {pinned.length > 0 && (
            <>
              <div className={styles.sectionDivider} />
              <p className={styles.sectionLabel}>
                {t('card.other_announcements')}
              </p>
            </>
          )}

          {unpinned.map(renderUnpinnedItem)}
        </>
      )}
    </InfoCard>
  );
}
